import { AfterContentChecked, ChangeDetectorRef, Component, effect, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { GuardsCheckEnd, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, ResolveEnd, ResolveStart, Router, RouterOutlet } from '@angular/router';

// RxJS
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

// Bootstrap
import { NgbModal, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';

// Components
import { HeaderComponent } from "./template/components/header/header.component";
import { SideMenuComponent } from "./template/components/side-menu/side-menu.component";
import { ToastsContainer } from './shared/components/toast-global/toasts-container.component';
import { LoaderComponent } from "./shared/components/loader/loader.component";
import { UtilizationModal } from './utilization/components/utilization-modal/utilization-modal.component';

// Services
import { AuthService } from './auth/services/auth.service';
import { BreakpointService } from './shared/services/breakpoint.service';
import { LoaderService } from './shared/services/loader.service';
import { QuotaAlerts } from './utilization/models/quota-alerts.model';
import { QuotaService } from './utilization/services/quota.service';
import { InactivityService } from './auth/services/inactivity.service';


@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    AsyncPipe,
    HeaderComponent,
    SideMenuComponent,
    NgbToastModule,
    ToastsContainer,
    LoaderComponent
  ],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterContentChecked {
  private readonly breakpointService = inject(BreakpointService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly modalSrv = inject(NgbModal);
  private readonly quotaSrv = inject(QuotaService);

  authSrv = inject(AuthService);
  loaderService = inject(LoaderService);

  currentBreakpoint$ = this.breakpointService.currentBreakpoint$;

  currentRoute: string = '';

  constructor() {
    inject(InactivityService);

    // Gestione loader durante la navigazione
    this.router.events
      .pipe(takeUntilDestroyed())
      .subscribe(event => {

        // 1. I guard sono stati valutati (compreso il tuo CanDeactivate)
        if (event instanceof GuardsCheckEnd) {
          if (event.shouldActivate) {
            // ok: i guard hanno dato esito positivo → si può mostrare il loader
            this.loaderService.show();
          } else {
            // i guard hanno bloccato la navigazione → NON mostrare loader
            this.loaderService.hide();
          }
        }

        // 2. Parte il resolve o la navigazione vera → loader già visibile
        else if (event instanceof ResolveStart) {
          this.loaderService.show();
        }

        // 3. Fine navigazione → sempre nascondere
        else if (
          event instanceof ResolveEnd ||
          event instanceof NavigationEnd ||
          event instanceof NavigationError ||
          event instanceof NavigationCancel // importantissimo!
        ) {
          this.loaderService.hide();
        }
      });

    effect(() => {
      const isLoggedIn = this.authSrv.isLoggedIn();
      if (isLoggedIn() === true) {
        this.checkQuotaAlerts();
      }
    });

    // TODO: Remove
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
      });
  }

  ngOnInit(): void {
    const accessToken = this.authSrv.getAccessToken();
    if (accessToken) {
      this.authSrv.setTokenInfo(accessToken);
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  checkQuotaAlerts(): void {
    this.quotaSrv.getQuotaAlerts().subscribe({
      next: (alerts) => {
        // recupera dal localStorage sino a quando l'utente ha scelto non mostrare più il modal
        const hideUtilizationModal = localStorage.getItem('hideUtilizationModalTo');
        if (hideUtilizationModal) {
          const hideUntil = new Date(hideUtilizationModal);
          if (new Date() < hideUntil) {
            // se la data è ancora valida, non mostrare il modal
            console.debug(`Utilization modal hidden until: ${hideUntil}`);
            return;
          } else {
            console.debug(`Utilization modal will be shown, hidden until: ${hideUntil}`);
            localStorage.removeItem('hideUtilizationModalTo');
          }
        }
        if (alerts.request.alertType !== null || alerts.storage.alertType !== null) {
          this.openUtilizationWarningModal(alerts);
        }
      }
    });
  }

  private openUtilizationWarningModal(alerts: QuotaAlerts) {
    const modalRef = this.modalSrv.open(UtilizationModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.alerts = alerts;
    modalRef.result.then(
      (result: { motivation: string }) => {
        console.debug(`Closed with: ${result}`);
      },
      (reason) => {
        console.debug(`Dismissed`);
      },
    );
  }

}
